import { useMemo, useState } from 'react';
import { PhoneTree } from '@weave/schema-gen-ts/dist/schemas/phone/tree/v1/phone_tree_service.pb';
import { useQuery } from 'react-query';
import { CallRouteV1 } from '@frontend/api-call-route';
import PhoneTreeAPI from '@frontend/api-phone-trees';
import { scopedFilterAndSort } from '@frontend/filters';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { usePhoneLocationFilterShallowStore, usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import {
  Chip,
  InlineNotification,
  Modal,
  Text,
  TextLink,
  Truncated,
  useAlert,
  useModalControl,
} from '@frontend/design-system';
import { styles } from '@frontend/design-system';
import { IndexTable } from '../../components/common/index-table';
import { DeletePhoneTreeIconButton } from '../../components/phone-tree-quick-actions/delete-button';
import { usePhoneTreeMutations } from '../../components/phone-tree-quick-actions/use-phone-tree-mutations';
import { PhoneSettingsPage } from '../../components/settings/settings-page';
import { VideoPlayer } from '../../layouts/video-player';
import { queryKeys } from '../../query-keys';
import { trackingId } from '../../tracking';
import { usePersistentPagination } from './usePersistentPagination';

export function PhoneTreeQuickActionsIndex() {
  const { navigate } = useSettingsNavigate();
  const { locationFilters, setLocationFilters } = usePhoneLocationFilterShallowStore(
    'locationFilters',
    'setLocationFilters'
  );
  const filteredLocations = locationFilters.phoneTreesIndex;
  const { settingsTenantLocation, globalAvailableLocationIds } = usePhoneSettingsShallowStore(
    'settingsTenantLocation',
    'globalAvailableLocationIds'
  );
  const { t } = useTranslation('phone');

  const [_pagination, _setPagination, paginationTableProps, paginationConfig] =
    usePersistentPagination('phone-trees-pagination');
  const { getScopeName } = useAppScopeStore();

  const [searchValue, setSearchValue] = useState('');
  const [showNotification, setShowNotification] = useState(true);
  const { modalProps, triggerProps } = useModalControl();
  const alerts = useAlert();

  const { data: phoneTrees = [], isLoading } = useQuery({
    queryKey: [settingsTenantLocation?.phoneTenantId, ...queryKeys.settings.listPhoneTrees()],
    queryFn: () =>
      PhoneTreeAPI.List({ tenantId: settingsTenantLocation?.phoneTenantId ?? '' }).then((res) => res.phoneTrees),
    enabled: !!settingsTenantLocation?.phoneTenantId,
    staleTime: 5 * 60 * 1000,
    meta: {
      error: {
        message: 'PhoneTreeQuickActionsIndex:phoneTreesQuery | Failed to fetch phone trees',
        data: { tenantId: settingsTenantLocation?.phoneTenantId },
      },
    },
  });

  const { data: routes = [], isLoading: callRoutesIsLoading } = CallRouteV1.Queries.useListQuery({
    request: {
      tenantId: settingsTenantLocation?.phoneTenantId ?? '',
      includes: { deviceExtensionRoutes: true },
    },
    options: {
      enabled: !!settingsTenantLocation?.phoneTenantId,
      select: ({ callRoutes, deviceExtensionRoutes }) => {
        return [...callRoutes, ...(deviceExtensionRoutes ?? [])];
      },
      cacheTime: 1000,
      staleTime: 1000,
      meta: {
        error: {
          message: 'PhoneTreeQuickActionsIndex:callRoutesQuery | Failed to fetch call routes',
          data: { tenantId: settingsTenantLocation?.phoneTenantId },
        },
      },
    },
  });

  const routeIdToLocationIdMap = useMemo(() => {
    const map: Record<string, string> = {};
    routes.forEach((route) => {
      if (!route.callRouteId || !route.locationId) return;
      map[route.callRouteId] = route.locationId;
    });
    return map;
  }, [routes]);

  const filteredPhoneTrees = useMemo(
    () =>
      scopedFilterAndSort({
        data: phoneTrees,
        scopeIds: globalAvailableLocationIds,
        filteredScopeIds: filteredLocations,
      }).filter(
        (phoneTree) =>
          phoneTree.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
          phoneTree.callRouteData?.name?.toLowerCase().includes(searchValue.toLowerCase())
      ),
    [phoneTrees, globalAvailableLocationIds, filteredLocations, searchValue]
  );

  const deletablePhoneTrees = useMemo(() => phoneTrees.filter((tree) => !tree.callRouteData?.id), [phoneTrees]);

  return (
    <PhoneSettingsPage
      title={t('Phone Tree Quick Actions')}
      action={
        <VideoPlayer
          wistiaVideoId='upq9ynsif0'
          buttonVariant='secondary'
          trackingId={trackingId({
            context: 'setting',
            feature: 'phone-trees-quick-actions',
            details: 'play-help-video-btn',
          })}
        />
      }
      subtitle={
        <div>
          <Trans t={t}>
            <Text css={{ maxWidth: '950px' }}>
              Use Phone Tree Quick Actions to update Phone Tree prompts, forwarding numbers, Voicemail Boxes, and
              Voicemail Greetings. To create a Phone Tree or to access advanced configuration, go to{' '}
              <TextLink
                onClick={() => navigate({ to: '/phone/call-routes' })}
                trackingId={trackingId({
                  context: 'setting',
                  feature: 'phone-trees-quick-actions',
                  details: 'call-routes-link',
                })}
              >
                Call Routes
              </TextLink>
              .
            </Text>
          </Trans>
          {showNotification && deletablePhoneTrees.length > 5 && (
            <div css={{ marginTop: 'var(--fabric-space-gap-lg)' }}>
              <InlineNotification status='info'>
                <div
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 'var(--fabric-space-gap-lg)',
                  }}
                >
                  <Text size='medium'>
                    {t('We noticed you have {{count}} Phone Trees outside of Call Routes. Clean up by deleting them?', {
                      count: deletablePhoneTrees.length,
                    })}
                  </Text>
                  <div css={{ display: 'flex', alignItems: 'center', gap: 'var(--fabric-space-gap-sm)' }}>
                    <TextLink
                      size='medium'
                      weight='bold'
                      {...triggerProps}
                      css={{
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      {t('Delete all {{count}}', { count: deletablePhoneTrees.length })}
                    </TextLink>
                    <Icon name='x' css={{ cursor: 'pointer' }} onClick={() => setShowNotification(false)} />
                  </div>
                </div>
              </InlineNotification>
              <BulkDeletePhoneTreesModal
                modalProps={modalProps}
                phoneTreesCount={deletablePhoneTrees.length}
                phoneTreeNames={deletablePhoneTrees.map((tree) => tree.name)}
                phoneTreeIds={deletablePhoneTrees.map((tree) => tree.id)}
                phoneTenantId={settingsTenantLocation?.phoneTenantId ?? ''}
                onSuccess={(deletedCount) => {
                  alerts.success(t('{{count}} Phone Trees deleted successfully.', { count: deletedCount }));
                  modalProps.onClose();
                }}
                onError={() => {
                  alerts.error(t('System failed to delete Phone Trees. Please try again.'));
                }}
              />
            </div>
          )}
        </div>
      }
      breadcrumbs={[
        { label: t('Phone'), to: '/phone/main' },
        { label: t('Phone Tree Quick Actions'), to: '/phone/phone-tree-quick-actions' },
      ]}
    >
      {settingsTenantLocation ? (
        <IndexTable
          {...paginationTableProps}
          labelsColumnPosition={2}
          tableConfig={{
            ...paginationConfig,
            hasGlobalSearch: true,
            globalSearchConfig: {
              searchHandler: setSearchValue,
            },
            colConfig: [
              {
                id: 'name',
                Header: t('Name'),
                accessor: 'name',
                sortType: 'string',
                cellRenderer: (name: string, phoneTree) => {
                  const id = phoneTree?.id;
                  const hasPrompt = Boolean(phoneTree?.prompt?.mediaItemId || phoneTree?.prompt?.mediaName);
                  const hasDialOptions = (phoneTree?.dialOptionCount ?? 0) > 0;
                  const missingDetailsLabel =
                    !hasPrompt && !hasDialOptions
                      ? t('(No prompt & dial options)')
                      : !hasPrompt
                      ? t('(No prompt)')
                      : !hasDialOptions
                      ? t('(No dial options)')
                      : null;

                  const overrides = phoneTree?.overrides ?? [];
                  const activeCount = overrides.filter((override) => override.enabled).length;
                  const scheduledCount = overrides.filter((override) => !override.enabled).length;

                  return (
                    <div
                      css={{ display: 'flex', alignItems: 'center', gap: 'var(--fabric-space-gap-xs)', width: '100%' }}
                    >
                      <div css={{ display: 'flex', alignItems: 'center', gap: 'var(--fabric-space-gap-xs)' }}>
                        <Truncated
                          as={TextLink}
                          textAlign='left'
                          onClick={() => {
                            navigate({ to: '/phone/phone-tree-quick-actions/:id', params: { id } });
                          }}
                          trackingId={trackingId({
                            context: 'setting',
                            feature: 'phone-trees-quick-actions',
                            details: 'table-phone-tree-link',
                          })}
                        >
                          {name || t('Unknown name')}
                        </Truncated>
                        {missingDetailsLabel && (
                          <Text size='medium' color='subdued' css={{ fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                            {missingDetailsLabel}
                          </Text>
                        )}
                      </div>
                      {activeCount > 0 && (
                        <Chip
                          variant='warn'
                          leftElement={<Icon name='voicemail-override' size={16} />}
                          tooltipLabel={t('This Phone Tree is used in {{count}} active override', {
                            count: activeCount,
                          })}
                        >
                          {t('{{count}} Active', { count: activeCount })}
                        </Chip>
                      )}
                      {scheduledCount > 0 && (
                        <Chip
                          leftElement={<Icon name='voicemail-override' size={16} />}
                          tooltipLabel={t('This Phone Tree is used in {{count}} scheduled override', {
                            count: scheduledCount,
                          })}
                        >
                          {t('{{count}} Scheduled', { count: scheduledCount })}
                        </Chip>
                      )}
                    </div>
                  );
                },
              },
              {
                id: 'callRoute',
                Header: t('Call Route'),
                accessor: (data) => data,
                sortType: (a, b) => {
                  // Default the name to "zzz" to sort "Not in Call Routes" at the end
                  const nameA = a.original.callRouteData?.name || 'zzz';
                  const nameB = b.original.callRouteData?.name || 'zzz';
                  return nameA.localeCompare(nameB);
                },
                cellRenderer: ({ callRouteData, id }: PhoneTree) => {
                  const callRouteId = callRouteData?.id;

                  return (
                    <>
                      {callRouteId ? (
                        <div css={{ width: '100%' }}>
                          <Truncated
                            as={TextLink}
                            textAlign='left'
                            onClick={() => {
                              navigate({
                                to: '/phone/call-routes/:id',
                                params: { id: callRouteId },
                                context: { contextPrimitiveId: id },
                              });
                            }}
                            trackingId={trackingId({
                              context: 'setting',
                              feature: 'phone-trees-quick-actions',
                              details: 'table-call-route-link',
                            })}
                          >
                            {callRouteData?.name || t('Manage Route')}
                          </Truncated>
                        </div>
                      ) : (
                        t('Not in Call Routes')
                      )}
                    </>
                  );
                },
              },
            ],
            data: filteredPhoneTrees,
            isLoading: isLoading || callRoutesIsLoading,
            emptyStateConfig: {
              type: 'sync_your_phone',
              header: t('No Phone Trees'),
              description: () =>
                !!phoneTrees?.length ? (
                  <Text textAlign='center'>{t('There are no Phone Trees to display with this filter setting.')}</Text>
                ) : (
                  <Trans t={t}>
                    <Text textAlign='center'>You don't have any phone trees yet. </Text>
                  </Trans>
                ),
            },
          }}
          tenantLocation={settingsTenantLocation}
          idAccessor={(data) => data.id}
          nameAccessor={(data) => data.name}
          labelsAccessor={(data) => data.locationIds}
          canDeleteRow={(data) => !data.callRouteData?.id}
          setFilteredLocations={(locations) => {
            setLocationFilters({ phoneTreesIndex: locations });
          }}
          filteredLocations={filteredLocations}
          DeleteIconButton={DeletePhoneTreeIconButton}
          locationsColumnConfig={{
            Header: t('Call Route Location'),
            accessor: (data) => data,
            cellRenderer: ({ callRouteData }: PhoneTree) => {
              const callRouteId = callRouteData?.id;
              const locationId = callRouteId ? routeIdToLocationIdMap[callRouteId] : null;

              if (!locationId) {
                return <span>--</span>;
              }

              const scopeName = getScopeName(locationId);

              return <Chip.SingleChip>{scopeName}</Chip.SingleChip>;
            },
          }}
        />
      ) : (
        <div>{t('No Phone Tenant Selected')}</div>
      )}
    </PhoneSettingsPage>
  );
}

const BulkDeletePhoneTreesModal = ({
  modalProps,
  phoneTreesCount,
  phoneTreeNames,
  phoneTreeIds,
  phoneTenantId,
  onSuccess,
  onError,
}: {
  modalProps: ReturnType<typeof useModalControl>['modalProps'];
  phoneTreesCount: number;
  phoneTreeNames: string[];
  phoneTreeIds: string[];
  phoneTenantId: string;
  onSuccess: (deletedCount: number) => void;
  onError: () => void;
}) => {
  const { t } = useTranslation('phone');
  const { remove } = usePhoneTreeMutations(phoneTenantId);
  const [isDeleting, setIsDeleting] = useState(false);

  const sortedPhoneTreeNames = useMemo(
    () => [...phoneTreeNames].sort((a, b) => (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' })),
    [phoneTreeNames]
  );

  const handleDeleteAll = async () => {
    setIsDeleting(true);
    try {
      const BATCH_SIZE = 50;
      let deletedCount = 0;
      for (let i = 0; i < phoneTreeIds.length; i += BATCH_SIZE) {
        const batch = phoneTreeIds.slice(i, i + BATCH_SIZE);
        const results = await Promise.allSettled(batch.map((id) => remove.mutateAsync({ phoneTreeId: id })));
        deletedCount += results.filter((r) => r.status === 'fulfilled').length;
      }
      if (deletedCount > 0) {
        onSuccess(deletedCount);
      } else {
        onError();
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal {...modalProps} minWidth={600}>
      <Modal.Header onClose={modalProps.onClose}>{t('Delete Phone Trees?')}</Modal.Header>
      <Modal.Body>
        <div css={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Text>
            {t('Are you sure you want to delete the following {{count}} Phone Trees?', {
              count: phoneTreesCount,
            })}
          </Text>
          <div
            css={[
              {
                maxHeight: '340px',
                overflowY: 'auto',
              },
              styles.smallScrollbar,
            ]}
          >
            {sortedPhoneTreeNames.map((name, index) => (
              <Text key={`${name}-${index}`} weight='bold'>
                {name || t('Unknown name')}
              </Text>
            ))}
          </div>
          <Text>{t('This action cannot be undone.')}</Text>
        </div>
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: t('Yes, Delete'),
          onClick: handleDeleteAll,
          loading: isDeleting,
          disabled: isDeleting,
          destructive: true,
        }}
        secondary={{
          label: t('No, Cancel'),
          onClick: modalProps.onClose,
        }}
      />
    </Modal>
  );
};
